import React, { useState } from 'react';
import { Box, Button, Typography, IconButton, Paper, Divider, Chip, Stack } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import HistoryIcon from '@mui/icons-material/History';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import DeleteIcon from '@mui/icons-material/Delete';
import MapIcon from '@mui/icons-material/Map';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import EditAttributesIcon from '@mui/icons-material/EditAttributes';
import TrendingFlatIcon from '@mui/icons-material/TrendingFlat';
import SatelliteIcon from '@mui/icons-material/Satellite';
import SearchIcon from '@mui/icons-material/Search';

const AnalysisSidebar = ({ 
    analyses, 
    onViewAnalysis, 
    activeResultIds, 
    onExportClick, 
    onClearBbox, 
    isBboxPresent, 
    onDownloadClick, 
    showCadastral, 
    onToggleCadastral,
    showOrthophoto,
    onToggleOrthophoto,
    showParcelSearch,
    onToggleParcelSearch
}) => {
    const [isOpen, setIsOpen] = useState(false);

    return (
        <>
            {!isOpen && (
                <Box sx={{ position: 'absolute', top: 100, left: 10, zIndex: 1000, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                    <Button variant="contained" startIcon={<HistoryIcon />} onClick={() => setIsOpen(true)} sx={sidebarButtonStyle('#2193b0', '#6dd5ed')}>
                        History
                    </Button>
                    <Button variant="contained" startIcon={isBboxPresent ? <DeleteIcon /> : <FileDownloadIcon />} onClick={isBboxPresent ? onClearBbox : onExportClick} sx={isBboxPresent ? sidebarButtonStyle('#f87171', '#dc2626') : sidebarButtonStyle('#2193b0', '#6dd5ed')}>
                        {isBboxPresent ? "Clear Box" : "Export"}
                    </Button>
                    {isBboxPresent && (
                        <Button variant="contained" startIcon={<FileDownloadIcon />} onClick={onDownloadClick} sx={sidebarButtonStyle('#10b981', '#059669')}>
                            Download
                        </Button>
                    )}
                    <Button variant="contained" startIcon={<MapIcon />} onClick={onToggleCadastral} sx={showCadastral ? sidebarButtonStyle('#9333ea', '#7e22ce') : sidebarButtonStyle('#6b7280', '#4b5563')}>
                        Cadastral
                    </Button>
                    <Button variant="contained" startIcon={<SatelliteIcon />} onClick={onToggleOrthophoto} sx={showOrthophoto ? sidebarButtonStyle('#f59e0b', '#d97706') : sidebarButtonStyle('#6b7280', '#4b5563')}>
                        Satellite
                    </Button>
                    <Button variant="contained" startIcon={<SearchIcon />} onClick={onToggleParcelSearch} sx={showParcelSearch ? sidebarButtonStyle('#10b981', '#059669') : sidebarButtonStyle('#6b7280', '#4b5563')}>
                        Search
                    </Button>
                </Box>
            )}

            {isOpen && (
                <Paper elevation={10} sx={{ position: 'absolute', top: 0, left: 0, height: '100%', width: 380, zIndex: 1200, display: 'flex', flexDirection: 'column', bgcolor: '#f8fafc' }}>
                    <Box sx={{ p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center', bgcolor: '#1e293b', color: '#fff' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <HistoryIcon />
                            <Typography variant="h6" fontWeight="700">Analysis History</Typography>
                        </Box>
                        <IconButton onClick={() => setIsOpen(false)} size="small" sx={{ color: '#fff' }}>
                            <CloseIcon />
                        </IconButton>
                    </Box>

                    <Box sx={{ flexGrow: 1, overflowY: 'auto', p: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
                        {analyses.map((item) => {
                            const isShowing = activeResultIds.includes(item.id);
                            const validOverrides = Object.entries(item.overriden_values || {})
                                .filter(([_, value]) => value !== null && value !== "");

                            return (
                                <Paper key={item.id} elevation={isShowing ? 4 : 1} sx={{ 
                                    p: 2, 
                                    borderRadius: '16px', 
                                    border: isShowing ? '3px solid #2193b0' : '1px solid #e2e8f0',
                                    transition: 'all 0.2s ease'
                                }}>
                                    {/* Header Meta Data */}
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1.5 }}>
                                        <Chip 
                                            label={item.status} 
                                            size="small" 
                                            sx={{ fontWeight: 800, height: 20, fontSize: '0.6rem', bgcolor: item.status === 'DONE' ? '#dcfce7' : '#fef3c7', color: item.status === 'DONE' ? '#166534' : '#92400e' }} 
                                        />
                                        <Typography variant="caption" color="text.secondary" fontWeight="500">
                                            {new Date(item.analysis_date).toLocaleDateString()}
                                        </Typography>
                                    </Box>

                                    <Typography variant="subtitle2" sx={{ display: 'flex', alignItems: 'center', gap: 0.5, fontWeight: 700, mb: 2 }}>
                                        <LocationOnIcon sx={{ fontSize: 16, color: '#ef4444' }} />
                                        {item.latitude.toFixed(5)}, {item.longitude.toFixed(5)}
                                    </Typography>

                                    {/* HIGH VISIBILITY OVERRIDE SECTION */}
                                    {validOverrides.length > 0 ? (
                                        <Box sx={{ 
                                            my: 2, 
                                            p: 1.5, 
                                            bgcolor: '#fff7ed', 
                                            borderRadius: '10px', 
                                            borderLeft: '5px solid #f97316',
                                            boxShadow: 'inset 0 1px 3px rgba(0,0,0,0.05)'
                                        }}>
                                            <Typography variant="caption" sx={{ 
                                                color: '#c2410c', 
                                                fontWeight: 900, 
                                                display: 'flex', 
                                                alignItems: 'center', 
                                                gap: 0.8,
                                                mb: 1,
                                                letterSpacing: '0.05rem',
                                                textTransform: 'uppercase'
                                            }}>
                                                <EditAttributesIcon sx={{ fontSize: 18 }} /> User Modifications
                                            </Typography>
                                            
                                            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1 }}>
                                                {validOverrides.map(([key, value]) => (
                                                    <Box key={key} sx={{ display: 'flex', flexDirection: 'column' }}>
                                                        <Typography sx={{ fontSize: '0.6rem', fontWeight: 700, color: '#9a3412', opacity: 0.8 }}>
                                                            {key}
                                                        </Typography>
                                                        <Typography sx={{ fontSize: '0.85rem', fontWeight: 800, color: '#1e293b' }}>
                                                            {value}
                                                        </Typography>
                                                    </Box>
                                                ))}
                                            </Box>
                                        </Box>
                                    ) : (
                                        <Box sx={{ my: 2, py: 1, px: 1.5, bgcolor: '#f1f5f9', borderRadius: '8px', border: '1px dashed #cbd5e1' }}>
                                            <Typography variant="caption" sx={{ color: '#94a3b8', fontWeight: 600 }}>
                                                Standard Analysis (No Overrides)
                                            </Typography>
                                        </Box>
                                    )}

                                    <Divider sx={{ mb: 2 }} />

                                    {/* Values Comparison */}
                                    <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
                                        <Box>
                                            <Typography variant="caption" color="text.secondary" fontWeight="700">ORIGINAL</Typography>
                                            <Typography variant="h6" fontWeight="600" color="#94a3b8">{item.original_value || '???'}</Typography>
                                        </Box>
                                        <TrendingFlatIcon sx={{ color: '#cbd5e1', fontSize: 30 }} />
                                        <Box textAlign="right">
                                            <Typography variant="caption" color="primary" fontWeight="700">NEW PIXEL</Typography>
                                            <Typography variant="h6" fontWeight="900" color="#1e293b">{item.pixel_value || '...'}</Typography>
                                        </Box>
                                    </Stack>

                                    <Button 
                                        fullWidth
                                        variant={isShowing ? "contained" : "outlined"} 
                                        color={isShowing ? "error" : "primary"} 
                                        startIcon={isShowing ? <VisibilityOffIcon /> : <VisibilityIcon />} 
                                        onClick={() => onViewAnalysis(item)}
                                        sx={{ borderRadius: '10px', fontWeight: 700, textTransform: 'none' }}
                                    >
                                        {isShowing ? "Hide Result" : "View on Map"}
                                    </Button>
                                </Paper>
                            );
                        })}
                    </Box>
                </Paper>
            )}
        </>
    );
};

const sidebarButtonStyle = (color1, color2) => ({
    background: `linear-gradient(135deg, ${color1} 0%, ${color2} 100%)`,
    color: 'white',
    textTransform: 'none',
    fontWeight: 700,
    borderRadius: '12px',
    boxShadow: '0 4px 14px 0 rgba(0, 0, 0, 0.2)',
    padding: '10px 20px',
    '&:hover': { transform: 'translateY(-2px)', transition: 'all 0.2s' }
});

export default AnalysisSidebar;